import styles from "./index.module.css";
import { useEffect, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import { useGetStatesQuery, useNewClientMutation } from "../../services/api";
import { newClientSchema } from "../../yup";
import CustomButton from "../Button/CustomButton";
import img from "../../assets/images/InceptionFinicallyLogo.png";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store/store";
import { AiOutlineArrowLeft } from "react-icons/ai";
import { tracker } from "../../services/mixpanel";
import SuccessModal from "./Success.modal";

interface State {
  _id: string;
  state: string;
}
interface StateOption {
  label: string;
  value: string;
}
const isMobile = window.innerWidth <= 768 || window.innerHeight <= 768;

const customStyles = isMobile
  ? {
      menuList: (base: any) => ({
        ...base,
        maxHeight: 120,
        overflowY: "auto",
        fontSize: "14px",
      }),
      option: (base: any, state: any) => ({
        ...base,
        whiteSpace: "wrap",
        overflow: "hidden",
        fontSize: "14px",
        backgroundColor: state.isFocused ? "#f0f0f0" : "white",
        color: "black",
      }),
      control: (base: any) => ({
        ...base,
        width: "100%",
        minWidth: 0,
      }),
    }
  : undefined;

const incomeOptions = [
  { label: "$250,000 - $1,000,000", value: "$250,000 - $1,000,000" },
  { label: "$1,000,000 - $5,000,000", value: "$1,000,000 - $5,000,000" },
  { label: "$5,000,000 - $10,000,000", value: "$5,000,000 - $10,000,000" },
  { label: "$10,000,000 +", value: "$10,000,000 +" },
];

const sourceOfIncomeOptions = [
  { label: "W2 Income", value: "W2 Income" },
  { label: "Sale of Stocks/Bonds", value: "Sale of Stocks/Bonds" },
  {
    label: "Sale of Company/Equity Event",
    value: "Sale of Company/Equity Event",
  },
  { label: "Other", value: "Other" },
];

const SignUpForm = () => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty },
    getValues,
  } = useForm({
    resolver: yupResolver(newClientSchema),
    mode: "onChange",
  });

  const navigate = useNavigate();
  const uniqueId = useAppSelector((state) => state.purlUser.uniqueId || "");
  const [newClient, { isLoading: loading }] = useNewClientMutation();
  const { data: states, isLoading, isSuccess } = useGetStatesQuery();
  const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);
  const [stateOptions, setStateOptions] = useState<StateOption[]>([]);
  const hasStarted = useRef(false);
  const isSubmittedRef = useRef(false);

  const handleBeforeUnload = () => {
    // if form is already submitted
    if (isSubmittedRef.current) {
      tracker.newClient.userCreated(getValues()?.email);
      return;
    }

    //don't track form-abandoned event on page refresh
    const entries = performance.getEntriesByType("navigation");
    const navEntry =
      entries.length > 0 ? (entries[0] as PerformanceNavigationTiming) : null;
    const isReload = navEntry?.type === "reload";
    if (isReload) return;

    tracker.newClient.formAbandoned();
  };

  const handleBackToDashboard = () => {
    handleBeforeUnload();
    navigate(`/${uniqueId}`);
  };

  const onError = () => {
    tracker.newClient.formError();
  };

  const onSubmit = async (values: any) => {
    tracker.newClient.formSubmit(values.email);
    try {
      let income: string[] = [];
      values.sourceOfIncome.map((item: StateOption) => income.push(item.value));
      const sendData: INewClientReq = {
        First_Name: values.firstName,
        Last_Name: values.lastName,
        Email: values.email,
        Mobile: values.mobilePhone,
        State_of_Residence: values.state.label,
        Annual_Income: values.annualIncome.value,
        Primary_Source_of_Income: JSON.stringify(income),
        Is_an_Advisor: values.isAdvisor === "Yes",
        Contact_Type: "Client",
        Contact_Group: "Inception Financial",
        Lead_Source: "Website",
      };

      const result = await newClient(sendData).unwrap();
      if (!result.success) {
        toast.error(result.data.message);
      }
      if (result.success) {
        isSubmittedRef.current = true;
        setShowConfirmationMessage(true);
      }
    } catch (err: any) {
      toast.error(err.data.message);
      console.error("Error:", err);
    }
  };

  const getStates = () => {
    const data = states?.data || [];
    const stateOption: StateOption[] = data.map((item: State) => {
      return {
        label: item.state,
        value: item._id,
      };
    });
    setStateOptions(stateOption);
  };

  useEffect(() => {
    if (isSubmittedRef.current) {
      // when modal is closed
      if(!showConfirmationMessage)
        window.location.replace("https://www.inception.financial/");

      // auto redirect after 10 sec
      const timer = setTimeout(() => {
        window.location.replace("https://www.inception.financial/");
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [showConfirmationMessage]);

  useEffect(() => {
    isSuccess && getStates();
  }, [states]);

  useEffect(() => {
    if (isDirty && !hasStarted.current) {
      hasStarted.current = true;
      tracker.newClient.formStarted();
    }
  }, [isDirty]);

  useEffect(() => {
    tracker.newClient.pageView();
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <div className={styles.content}>
      <div className={styles.container}>
        <div className=" flex justify-between items-center w-full px-10 pb-20 absolute top-0 left-0">
          <a href="https://www.inception.financial/">
            <img src={img} alt="" className={styles.image} />
          </a>
          <button
            className="flex items-center p-2 text-white bg-green-800 hover:bg-green-800 rounded-lg shadow-md h-10"
            onClick={handleBackToDashboard}
          >
            <AiOutlineArrowLeft className="mr-2 text-lg" />
            Back
          </button>
        </div>
        <div className={styles.signupForm}>
          <form onSubmit={handleSubmit(onSubmit, onError)}>
            <div className={styles.inputBox}>
              <div className={styles.headerRow}>
                <h2>Consult with Us</h2>
              </div>
              <div className={styles.inputRow}>
                <div className="w-full mx-2">
                  <input
                    placeholder="FIRST NAME"
                    type="text"
                    className={styles.allInput}
                    {...register("firstName")}
                    name="firstName"
                    autoComplete="off"
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-[14px] text-left">
                      {errors.firstName.message}
                    </p>
                  )}
                </div>
                <div className="w-full mx-2">
                  <input
                    placeholder="LAST NAME"
                    type="text"
                    className={styles.allInput}
                    {...register("lastName")}
                    name="lastName"
                    autoComplete="off"
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-[14px] text-left">
                      {errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>
              <div className={styles.inputRow}>
                <div className="w-full mx-2">
                  <input
                    placeholder="MOBILE PHONE"
                    type="string"
                    className={styles.allInput}
                    {...register("mobilePhone")}
                    name="mobilePhone"
                    autoComplete="off"
                  />
                  {errors.mobilePhone && (
                    <p className="text-red-500 text-[14px] text-left">
                      {errors.mobilePhone.message}
                    </p>
                  )}
                </div>
                <div className="w-full mx-2">
                  <input
                    placeholder="EMAIL ADDRESS"
                    type="email"
                    className={styles.allInput}
                    {...register("email")}
                    name="email"
                    autoComplete="off"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-[14px] text-left">
                      {errors.email.message}
                    </p>
                  )}
                </div>
              </div>
              <div className={styles.inputRow}>
                <div className="w-full mx-2">
                  <Controller
                    name="state"
                    control={control}
                    render={({ field }: any) => (
                      <Select
                        {...field}
                        className={`${styles.selectInput} `}
                        classNamePrefix="addl-class"
                        placeholder="STATE OF RESIDENCE"
                        isLoading={isLoading}
                        options={stateOptions}
                        styles={customStyles}
                        onChange={(selectedOption) =>
                          field.onChange(selectedOption)
                        }
                      />
                    )}
                  />
                  {errors.state && (
                    <p className="text-red-500 text-[14px] text-left">
                      {errors.state.message}
                    </p>
                  )}
                </div>
                <div className="w-full mx-2">
                  <Controller
                    name="annualIncome"
                    control={control}
                    render={({ field }: any) => (
                      <Select
                        {...field}
                        className={`${styles.selectInput} `}
                        classNamePrefix="addl-class"
                        placeholder="ANNUAL INCOME"
                        isLoading={isLoading}
                        styles={customStyles}
                        options={incomeOptions}
                        onChange={(selectedOption) =>
                          field.onChange(selectedOption)
                        }
                      />
                    )}
                  />
                  {errors.annualIncome && (
                    <p className="text-red-500 text-[14px] text-left">
                      {errors.annualIncome.message}
                    </p>
                  )}
                </div>
              </div>
              <div className={styles.inputRow}>
                <div className="w-full mx-2">
                  <Controller
                    name="sourceOfIncome"
                    control={control}
                    render={({ field }: any) => (
                      <Select
                        {...field}
                        className={`${styles.selectInput} `}
                        classNamePrefix="addl-class"
                        isLoading={isLoading}
                        placeholder="PRIMARY SOURCE OF INCOME"
                        options={sourceOfIncomeOptions}
                        styles={customStyles}
                        isMulti
                        onChange={(selectedOption) =>
                          field.onChange(selectedOption)
                        }
                      />
                    )}
                  />
                  {errors.sourceOfIncome && (
                    <p className="text-red-500 text-[14px] text-left">
                      {errors.sourceOfIncome.message}
                    </p>
                  )}
                </div>
              </div>
              <div className={styles.inputRow}>
                <div className={styles.advisorContainer}>
                  <p>I am an advisor or affiliated with an advisory firm:</p>
                  <div className={styles.radioButtonGroup}>
                    <input
                      type="radio"
                      id="Yes"
                      {...register("isAdvisor")}
                      name="isAdvisor"
                      value="Yes"
                    />
                    <label htmlFor="Yes">Yes</label>
                    <input
                      type="radio"
                      {...register("isAdvisor")}
                      id="No"
                      value="No"
                      name="isAdvisor"
                    />
                    <label htmlFor="No">No</label>
                  </div>
                  {errors.isAdvisor && (
                    <p className="text-red-500 text-[14px] text-left">
                      {errors.isAdvisor.message}
                    </p>
                  )}
                </div>
                <CustomButton
                  buttonLabel="SUBMIT"
                  isLoading={loading}
                  style={{ width: "160px", marginRight: "8px" }}
                />
              </div>
            </div>
          </form>
          <SuccessModal 
            open={showConfirmationMessage}
            onClose={() => setShowConfirmationMessage(false)}
          />
          {/* {showConfirmationMessage && (
            <div className="w-full">
              <hr className="text-white" />
              <div className="text-3xl text-white leading-10 text-center py-6">
                A member of our client relations team will review your
                submission and you will hear from us if there is an opportunity
                to participate in the strategy.
              </div>
              <hr className="text-white" />
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default SignUpForm;
